import React, { useRef, useEffect, useState } from 'react';
import cytoscape from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';
import { toast } from 'react-hot-toast';
import styles from './WatcherAssets.module.css';

cytoscape.use(coseBilkent);

const NetworkTrafficModal = ({ 
    isOpen, 
    onClose, 
    watcherId,
    watcherName 
}) => {
    const cyRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [cy, setCy] = useState(null);
    
    // Add ref to track current hovered node and abort controllers
    const currentHoverRef = useRef(null);
    const abortControllersRef = useRef([]);
    
    // Control states
    const [portLimit, setPortLimit] = useState('1048');
    const [connectionLimit, setConnectionLimit] = useState('200');
    const [includeInternalOnly, setIncludeInternalOnly] = useState(false);
    const [includeExternalOnly, setIncludeExternalOnly] = useState(false);
    // 'ip' = device node map, 'zone' = zone-to-zone communication.
    const [viewMode, setViewMode] = useState('ip');
    const [layoutName] = useState('cose-bilkent'); // Changed from 'grid' to 'cose-bilkent'
    
    // Data and statistics
    const [networkData, setNetworkData] = useState({ nodes: [], edges: [] });
    const [statistics, setStatistics] = useState({});
    
    // UI states
    const [showControls, setShowControls] = useState(true);
    const [showLegend, setShowLegend] = useState(true);
    const [tooltip, setTooltip] = useState({
        visible: false,
        x: 0,
        y: 0,
        content: null
    });
    
    // Add state for enhanced data
    const [assetDetailsCache, setAssetDetailsCache] = useState({});

    // Simplified and robust function to extract hostname from label
    const extractHostnameFromLabel = (label) => {
        if (!label) return null;
        
        // Use regex to find content within parentheses
        const match = label.match(/\(([^)]+)\)/);
        return match ? match[1].trim() : null;
    };

    // Updated function to get display hostname
    const getDisplayHostname = (nodeData) => {
        // Extract from label first (primary source)
        const extracted = extractHostnameFromLabel(nodeData.label);
        
        if (extracted) {
            // Return the extracted value (including IP if that's what's in parentheses)
            return extracted;
        }
        
        // Fallback to API hostname field if available and different from IP
        if (nodeData.hostname && nodeData.hostname !== nodeData.ip) {
            return nodeData.hostname;
        }
        
        // No hostname data found
        return null;
    };

    // Updated function to determine hostname styling
    const getHostnameDisplayClass = (nodeData) => {
        const extracted = extractHostnameFromLabel(nodeData.label);
        if (extracted === nodeData.ip) {
            return 'tooltip_ip'; // Use the same styling as IP addresses when showing IP
        }
        return 'tooltip_hostname'; // Use hostname styling for actual hostnames
    };

    const getNodeSizeCategory = (size) => {
        switch(size) {
            case 'large': return 'Large (High Activity)';
            case 'medium': return 'Medium (Moderate Activity)';
            case 'small': return 'Small (Low Activity)';
            default: return 'Unknown';
        }
    };

    const getConnectionStrength = (connectionCount) => {
        if (connectionCount >= 100) return 'Very High';
        if (connectionCount >= 50) return 'High';
        if (connectionCount >= 20) return 'Medium';
        if (connectionCount >= 5) return 'Low';
        return 'Very Low';
    };

    // Add this new function
    const getZoneColor = (zone) => {
        if (!zone) return '#999'; // Default gray for no zone
        
        // Hash the zone name to get a consistent color
        let hash = 0;
        for (let i = 0; i < zone.length; i++) {
            hash = zone.charCodeAt(i) + ((hash << 5) - hash);
        }
        
        // Generate a color from the hash
        const hue = Math.abs(hash % 360);
        return `hsl(${hue}, 65%, 50%)`;
    };

    const formatProtocolsList = (protocols) => {
        if (!protocols || protocols.length === 0) return [];
        
        // Group protocols by type for better display
        const protocolGroups = {};
        protocols.forEach(protocol => {
            const [proto, port] = protocol.split(':');
            if (!protocolGroups[proto]) {
                protocolGroups[proto] = [];
            }
            protocolGroups[proto].push(port);
        });

        return Object.entries(protocolGroups).map(([proto, ports]) => ({
            protocol: proto.toUpperCase(),
            ports: ports.sort((a, b) => parseInt(a) - parseInt(b))
        }));
    };

    // Enhanced tooltip positioning to prevent off-screen display
    const getTooltipPosition = (x, y, tooltipWidth = 350, tooltipHeight = 300) => {
        const container = cyRef.current;
        if (!container) return { x: x + 10, y: y - 10 };

        const containerRect = container.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        let adjustedX = x + 15;
        let adjustedY = y - 10;

        // Prevent tooltip from going off the right edge
        if (adjustedX + tooltipWidth > containerWidth) {
            adjustedX = x - tooltipWidth - 15;
        }

        // Prevent tooltip from going off the bottom edge
        if (adjustedY + tooltipHeight > containerHeight) {
            adjustedY = y - tooltipHeight + 10;
        }

        // Prevent tooltip from going off the left edge
        if (adjustedX < 0) {
            adjustedX = 15;
        }

        // Prevent tooltip from going off the top edge
        if (adjustedY < 0) {
            adjustedY = 15;
        }

        return { x: adjustedX, y: adjustedY };
    };

    useEffect(() => {
        if (isOpen && cyRef.current) {
            const timer = setTimeout(() => {
                initializeNetwork();
            }, 50);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (cy && networkData.nodes.length > 0) {
            updateVisualization();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cy, networkData, layoutName]);

    // Apply the Internal/External filter the moment a checkbox toggles, rather
    // than making the operator also click Refresh. Skip the mount run — the
    // isOpen effect does the initial load.
    const didMountRef = useRef(false);
    useEffect(() => {
        // Skip the mount run (the isOpen effect does the initial load); mark it
        // BEFORE the open guard so a modal that mounts closed still counts.
        if (!didMountRef.current) { didMountRef.current = true; return; }
        if (!isOpen || !cyRef.current) return;
        initializeNetwork();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includeInternalOnly, includeExternalOnly, viewMode]);

    const fetchNetworkConnections = async () => {
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('watcherId', watcherId);
            formData.append('portLimit', portLimit);
            formData.append('connectionLimit', connectionLimit);
            formData.append('includeInternalOnly', includeInternalOnly.toString());
            formData.append('includeExternalOnly', includeExternalOnly.toString());

            // Both views use the same device node map; Zone view only recolours
            // the dots by each node's zone (node.zoneColor).
            const response = await fetch('/api/network-connections', {
                method: 'POST',
                body: formData
            });

            const data = await response.json();
            if (data.status) {
                return data;
            } else {
                toast.error(data.msg || 'Failed to fetch network connections');
                return null;
            }
        } catch (error) {
            console.error('Error fetching network connections:', error);
            toast.error('Failed to fetch network connections');
            return null;
        } finally {
            setLoading(false);
        }
    };

    const transformDataForCytoscape = (apiData) => {
        const nodes = apiData.nodes.map(node => ({
            data: {
                id: node.id,
                label: node.label,
                ip: node.ip,
                hostname: node.hostname,
                type: node.type,
                zone: node.zone,
                // Zone view fills the dot by zone; Node view leaves it to the
                // internal/external classes (green / red).
                color: viewMode === 'zone' ? node.zoneColor : undefined,
                totalConnections: node.totalConnections,
                lastActivity: node.lastActivity,
                size: node.size
            },
            classes: node.type // 'internal' or 'external'
        }));

        // Group edges by source-target pair
        const edgeGroups = {};
        apiData.edges.forEach(edge => {
            const key = `${edge.source}-${edge.target}`;
            if (!edgeGroups[key]) {
                edgeGroups[key] = {
                    id: key,
                    source: edge.source,
                    target: edge.target,
                    protocols: [],
                    totalConnections: 0,
                    lastSeen: edge.lastSeen,
                    firstSeen: edge.firstSeen
                };
            }
            edgeGroups[key].protocols.push(`${edge.protocol}:${edge.port}`);
            edgeGroups[key].totalConnections += edge.connectionCount;
            // Keep the most recent lastSeen
            if (new Date(edge.lastSeen) > new Date(edgeGroups[key].lastSeen)) {
                edgeGroups[key].lastSeen = edge.lastSeen;
            }
        });

        const edges = Object.values(edgeGroups).map(group => ({
            data: {
                id: group.id,
                source: group.source,
                target: group.target,
                label: group.protocols.length > 3 
                    ? `${group.protocols.slice(0, 3).join(', ')}... (+${group.protocols.length - 3})`
                    : group.protocols.join(', '),
                protocols: group.protocols,
                connectionCount: group.totalConnections,
                lastSeen: group.lastSeen,
                firstSeen: group.firstSeen,
                // Much more subtle edge thickness scaling
                weight: Math.max(1, Math.min(group.totalConnections / 8, 6)),
                // Much more subtle arrow scaling - keep arrows reasonably sized
                arrowScale: Math.max(0.9, Math.min(group.totalConnections / 15, 1.8))
            }
        }));

        return { nodes, edges };
    };

    // Helper function to cancel all pending requests
    const cancelPendingRequests = () => {
        abortControllersRef.current.forEach(controller => {
            try {
                controller.abort();
            } catch (e) {
                // Ignore abort errors
            }
        });
        abortControllersRef.current = [];
    };

    const initializeNetwork = async () => {
        if (!cyRef.current || !watcherId) return;

        // Clear existing cytoscape instance
        if (cy) {
            cy.destroy();
        }

        const apiData = await fetchNetworkConnections();
        if (!apiData) return;

        const transformedData = transformDataForCytoscape(apiData);
        setNetworkData(transformedData);
        setStatistics(apiData.statistics || {});

        // Initialize cytoscape
        const newCy = cytoscape({
            container: cyRef.current,
            elements: [...transformedData.nodes, ...transformedData.edges],
            style: [
                {
                    selector: 'node',
                    style: {
                        'label': 'data(label)',
                        'text-valign': 'center',
                        'text-halign': 'center',
                        'font-size': '10px',
                        'font-weight': 'bold',
                        'text-wrap': 'wrap',
                        'text-max-width': '80px',
                        'color': '#fff',
                        'text-outline-width': 2,
                        'text-outline-color': '#000',
                        // Fixed size for all nodes
                        'width': 40,
                        'height': 40,
                        'border-width': 2,
                        'border-opacity': 0.8
                    }
                },
                {
                    selector: 'node.internal',
                    style: {
                        'background-color': '#4CAF50',
                        'border-color': '#2E7D32'
                    }
                },
                {
                    selector: 'node.external',
                    style: {
                        'background-color': '#F44336',
                        'border-color': '#C62828'
                    }
                },
                {
                    // Zone view: fill each dot with the zone's colour (the backend
                    // sends it; IP nodes have none, so this rule only hits zones).
                    selector: 'node[color]',
                    style: {
                        'background-color': 'data(color)',
                        'border-color': 'data(color)'
                    }
                },
                {
                    selector: 'edge',
                    style: {
                        'width': 'data(weight)',
                        'line-color': '#666',
                        'target-arrow-color': '#666',
                        'target-arrow-shape': 'triangle',
                        'arrow-scale': 'data(arrowScale)', // Changed from 'target-arrow-scale'
                        'curve-style': 'bezier',
                        'label': '', // Hide labels by default
                        'font-size': '8px',
                        'opacity': 0.7
                    }
                },
                {
                    selector: 'edge:selected',
                    style: {
                        'line-color': '#FFD700',
                        'target-arrow-color': '#FFD700',
                        'width': ele => Math.max(3, ele.data('weight') * 1.2), // Slightly thicker when selected
                        'arrow-scale': ele => Math.max(1.2, ele.data('arrowScale') * 1.1), // Slightly bigger when selected
                        'opacity': 1,
                        'label': 'data(label)',
                        'color': '#333',
                        'text-outline-width': 1,
                        'text-outline-color': '#fff'
                    }
                },
                {
                    selector: 'node:selected',
                    style: {
                        'border-width': 4,
                        'border-color': '#FFD700',
                        'box-shadow': '0 0 20px #FFD700'
                    }
                },
                {
                    selector: 'node[zone]',
                    style: {
                        'border-width': 3,
                        'border-color': ele => getZoneColor(ele.data('zone'))
                    }
                }
            ],
            layout: {
                name: 'cose-bilkent',
                animate: true,
                animationDuration: 1000,
                randomize: true,
                nodeRepulsion: 10000,               
                idealEdgeLength: 150,               
                edgeElasticity: 0.45,
                nestingFactor: 0.1,
                gravity: 0.4,                       
                numIter: 2500,
                tile: false,
                tilingPaddingVertical: 10,
                tilingPaddingHorizontal: 10
            },
            zoomingEnabled: true,
            userZoomingEnabled: true,
            panningEnabled: true,
            userPanningEnabled: true,
            boxSelectionEnabled: true,
            selectionType: 'single',
            touchTapThreshold: 8,
            desktopTapThreshold: 4,
            autolock: false,
            autoungrabify: false,
            autounselectify: false
        });

        newCy.on('mouseover', 'node', async (event) => {
            const node = event.target;
            const renderedPos = node.renderedPosition();
            const position = getTooltipPosition(renderedPos.x, renderedPos.y);
            const nodeIp = node.data('ip');
            
            // Cancel any pending requests from previous hovers
            cancelPendingRequests();
            
            // Set current hovered node
            currentHoverRef.current = nodeIp;
            
            // Set initial tooltip immediately
            setTooltip({
                visible: true,
                x: position.x,
                y: position.y,
                content: {
                    type: 'node',
                    data: node.data(),
                    assetDetails: assetDetailsCache[nodeIp] || null,
                    loading: !assetDetailsCache[nodeIp]
                }
            });
            
            // Fetch asset details if not cached (both views use real IPs now).
            if (!assetDetailsCache[nodeIp]) {
                const abortController = new AbortController();
                abortControllersRef.current.push(abortController);
                
                try {
                    const details = await fetchAssetDetails(nodeIp, abortController.signal);
                    
                    // Only update if we're still hovering on this node
                    if (currentHoverRef.current !== nodeIp) return;
                    
                    if (details && details.status) {
                        const assetInfo = details.trafficRows && details.trafficRows[0] ? {
                            mac: details.trafficRows[0][2] || 'N/A',
                            vendor: details.trafficRows[0][3] || 'N/A',
                            hostname: details.trafficRows[0][4] || 'N/A',
                            os: details.trafficRows[0][5] || 'N/A',
                            services: details.trafficRows[0][6] || 'N/A',
                            zone: details.trafficRows[0][7] || 'N/A',
                            switchInfo: details.trafficRows[0][8] || 'N/A',
                            note: details.trafficRows[0][9] || 'N/A'
                        } : null;
                        
                        setAssetDetailsCache(prev => ({
                            ...prev,
                            [nodeIp]: assetInfo
                        }));
                        
                        // Update tooltip with asset details - check again before updating
                        if (currentHoverRef.current === nodeIp) {
                            setTooltip(current => ({
                                ...current,
                                content: {
                                    ...current.content,
                                    assetDetails: assetInfo,
                                    loading: false
                                }
                            }));
                        }
                    }
                } catch (error) {
                    if (error.name === 'AbortError') {
                        // Request was cancelled, ignore
                        return;
                    }
                    console.error('Error fetching asset details:', error);
                }
            }
        });

        newCy.on('mouseover', 'edge', (event) => {
            const edge = event.target;
            const renderedPos = edge.renderedMidpoint();
            const position = getTooltipPosition(renderedPos.x, renderedPos.y);
            
            setTooltip({
                visible: true,
                x: position.x,
                y: position.y,
                content: {
                    type: 'edge',
                    data: edge.data()
                }
            });
        });

        newCy.on('mouseout', 'node, edge', () => {
            // Clear current hover reference
            currentHoverRef.current = null;
            // Cancel any pending requests
            cancelPendingRequests();
            setTooltip({ visible: false, x: 0, y: 0, content: null });
        });

        setCy(newCy);
    };

    const updateVisualization = () => {
        if (!cy) return;

        const layout = cy.layout({
            name: layoutName === 'cose-bilkent' ? 'cose-bilkent' : 'grid',
            animate: true,
            animationDuration: 1000,
            ...(layoutName === 'cose-bilkent' ? {
                randomize: true,
                nodeRepulsion: 10000,       // Increased from 6500 - much more space between nodes
                idealEdgeLength: 150,       // Increased from 100 - longer edges for better separation
                edgeElasticity: 0.45,
                nestingFactor: 0.1,
                gravity: 0.4,               // Reduced from 0.6 - allow even more spread
                numIter: 2500,
                tile: false
            } : {})
        });

        // Fit the graph to the pane once the layout settles. Without this the
        // first render left every node piled in the top-left corner until the
        // user clicked "Reset View", because the initial layout ran before the
        // map container had its real size.
        layout.one('layoutstop', () => {
            const settle = () => { cy.resize(); cy.fit(50); cy.center(); };
            settle();
            // Re-fit once the modal/container has its final size — on a view
            // switch the first fit can run against a stale (corner) size.
            setTimeout(settle, 80);
        });

        layout.run();
    };

    const refreshData = async () => {
        await initializeNetwork();
        toast.success('Network data refreshed');
    };

    const exportData = () => {
        if (!cy) return;
        
        const png = cy.png({ scale: 2, full: true });
        const link = document.createElement('a');
        link.download = `network-traffic-${watcherName}-${new Date().toISOString().split('T')[0]}.png`;
        link.href = png;
        link.click();
    };

    const resetView = () => {
        if (cy) {
            cy.fit();
            cy.center();
        }
    };

    // Update fetchAssetDetails to accept AbortSignal
    const fetchAssetDetails = async (deviceIP, signal) => {
        const formData = new FormData();
        formData.append('ipAddress', deviceIP);
        formData.append('watcherId', watcherId);
        
        const response = await fetch('/api/ip-traffic-details', {
            method: 'POST',
            body: formData,
            signal: signal
        });
        
        return await response.json();
    };

    if (!isOpen) return null;

    return (
        <div className={styles.network_modal_content}>
            {/* Header */}
            <div className={styles.modal_header}>
                <div>
                    <h2 style={{ margin: 0, color: 'var(--primary-color)' }}>
                        {viewMode === 'zone' ? 'Network Traffic — Coloured by Zone' : 'Network Traffic Flow'} - {watcherName}
                    </h2>
                    {statistics && (
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                            {`${statistics.totalUniqueIPs ?? 0} IPs • ${statistics.totalUniqueFlows ?? 0} Flows • ${statistics.internalIPs ?? 0} Internal • ${statistics.externalIPs ?? 0} External`}
                        </div>
                    )}
                </div>
                
                {/* Controls in Header */}
                {showControls && (
                    <div className={styles.network_controls}>
                        <div className={styles.filter_controls}>
                            <div className={styles.limit_control}>
                                <span>Port Limit:</span>
                                <input
                                    type="number"
                                    value={portLimit}
                                    onChange={(e) => setPortLimit(e.target.value)}
                                    className={styles.limit_select}
                                    min="1"
                                    max="65535"
                                />
                            </div>

                            <div className={styles.limit_control}>
                                <span>Connections:</span>
                                <input
                                    type="number"
                                    value={connectionLimit}
                                    onChange={(e) => setConnectionLimit(e.target.value)}
                                    className={styles.limit_select}
                                    min="1"
                                    max="10000"
                                />
                            </div>

                            <label className={styles.filter_label}>
                                <input
                                    type="checkbox"
                                    checked={includeInternalOnly}
                                    onChange={(e) => {
                                        setIncludeInternalOnly(e.target.checked);
                                        if (e.target.checked) setIncludeExternalOnly(false);
                                    }}
                                />
                                Internal Only
                            </label>

                            <label className={styles.filter_label}>
                                <input
                                    type="checkbox"
                                    checked={includeExternalOnly}
                                    onChange={(e) => {
                                        setIncludeExternalOnly(e.target.checked);
                                        if (e.target.checked) setIncludeInternalOnly(false);
                                    }}
                                />
                                External Only
                            </label>
                        </div>

                        <div className={styles.enhanced_controls}>
                            <button
                                onClick={() => setViewMode(m => (m === 'zone' ? 'ip' : 'zone'))}
                                disabled={loading}
                                className={styles.control_button}
                                style={{ backgroundColor: viewMode === 'zone' ? '#a20000' : '#176576' }}
                                title="Switch between the device node map and zone-to-zone communication"
                            >
                                {viewMode === 'zone' ? 'Node View' : 'Zone View'}
                            </button>

                            <button
                                onClick={refreshData}
                                disabled={loading}
                                className={styles.control_button}
                            >
                                {loading ? 'Loading...' : 'Refresh'}
                            </button>

                            <button
                                onClick={resetView}
                                className={styles.control_button}
                                style={{ backgroundColor: '#666' }}
                            >
                                Reset View
                            </button>

                            <button
                                onClick={exportData}
                                className={styles.control_button}
                                style={{ backgroundColor: '#4CAF50' }}
                            >
                                Export PNG
                            </button>

                            <button
                                onClick={() => setShowControls(false)}
                                className={styles.control_button}
                                style={{ backgroundColor: 'transparent', color: 'var(--text-primary)', border: '1px solid #ccc' }}
                            >
                                Hide Controls
                            </button>
                        </div>
                    </div>
                )}

                <button 
                    onClick={onClose}
                    className={styles.modal_close_button}
                >
                    ×
                </button>
            </div>

            {!showControls && (
                <button
                    onClick={() => setShowControls(true)}
                    className={styles.control_button}
                    style={{
                        position: 'absolute',
                        top: '90px',
                        right: '20px',
                        zIndex: 1000
                    }}
                >
                    Show Controls
                </button>
            )}

            {/* Network Visualization */}
            <div className={styles.network_modal_body}>
                <div className={styles.network_container}>
                    <div 
                        ref={cyRef} 
                        style={{ 
                            width: '100%', 
                            height: '100%',
                            minHeight: '600px'
                        }}
                    />

                    {/* Loading Overlay */}
                    {loading && (
                        <div className={styles.loading_spinner_container}>
                            <div className={styles.loading_spinner}></div>
                            <p>Loading network data...</p>
                        </div>
                    )}

                    {/* Enhanced Comprehensive Tooltip */}
                    {tooltip.visible && tooltip.content && (
                        <div 
                            className={styles.enhanced_tooltip}
                            style={{
                                left: tooltip.x,
                                top: tooltip.y
                            }}
                        >
                            {tooltip.content.type === 'node' ? (
                                // Enhanced Node Tooltip
                                <>
                                    <div className={styles.enhanced_tooltip_header}>
                                        <div className={styles.tooltip_icon}>🖥️</div>
                                        <div>
                                            <div className={styles.tooltip_title}>Network Node</div>
                                            <div className={styles.tooltip_subtitle}>
                                                {tooltip.content.data.type === 'internal' ? 'Internal Network' : 'External Network'}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className={styles.enhanced_tooltip_content}>
                                        {/* Primary Information Section */}
                                        <div className={styles.tooltip_section}>
                                            <div className={styles.tooltip_section_title}>Primary Information</div>
                                            <div className={styles.tooltip_row}>
                                                <span className={styles.tooltip_label}>IP Address:</span>
                                                <span className={`${styles.tooltip_value} ${styles.tooltip_ip}`}>
                                                    {tooltip.content.data.ip}
                                                </span>
                                            </div>
                                            <div className={styles.tooltip_row}>
                                                <span className={styles.tooltip_label}>Hostname/FQDN:</span>
                                                <span className={`${styles.tooltip_value} ${
                                                    getDisplayHostname(tooltip.content.data) 
                                                        ? styles[getHostnameDisplayClass(tooltip.content.data)]
                                                        : styles.tooltip_na
                                                }`}>
                                                    {getDisplayHostname(tooltip.content.data) || 'Not Available'}
                                                </span>
                                            </div>
                                            <div className={styles.tooltip_row}>
                                                <span className={styles.tooltip_label}>Node Type:</span>
                                                <span className={`${styles.tooltip_value} ${
                                                    tooltip.content.data.type === 'internal' 
                                                        ? styles.tooltip_internal 
                                                        : styles.tooltip_external
                                                }`}>
                                                    {tooltip.content.data.type === 'internal' ? 'Internal' : 'External'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Asset Details Section - NEW */}
                                        {tooltip.content.assetDetails && (
                                            <div className={styles.tooltip_section}>
                                                <div className={styles.tooltip_section_title}>Asset Information</div>
                                                <div className={styles.tooltip_row}>
                                                    <span className={styles.tooltip_label}>MAC Address:</span>
                                                    <span className={`${styles.tooltip_value} ${styles.tooltip_hostname}`}>
                                                        {tooltip.content.assetDetails.mac}
                                                    </span>
                                                </div>
                                                <div className={styles.tooltip_row}>
                                                    <span className={styles.tooltip_label}>Vendor:</span>
                                                    <span className={styles.tooltip_value}>
                                                        {tooltip.content.assetDetails.vendor}
                                                    </span>
                                                </div>
                                                <div className={styles.tooltip_row}>
                                                    <span className={styles.tooltip_label}>OS:</span>
                                                    <span className={styles.tooltip_value}>
                                                        {tooltip.content.assetDetails.os}
                                                    </span>
                                                </div>
                                                {tooltip.content.assetDetails.services && tooltip.content.assetDetails.services !== 'N/A' && (
                                                    <div className={styles.tooltip_row}>
                                                        <span className={styles.tooltip_label}>Services:</span>
                                                        <span className={`${styles.tooltip_value} ${styles.tooltip_ports}`}>
                                                            {tooltip.content.assetDetails.services}
                                                        </span>
                                                    </div>
                                                )}
                                                {tooltip.content.assetDetails.zone && tooltip.content.assetDetails.zone !== 'N/A' && (
                                                    <div className={styles.tooltip_row}>
                                                        <span className={styles.tooltip_label}>Zone/Site:</span>
                                                        <span className={`${styles.tooltip_value} ${styles.tooltip_protocol}`}>
                                                            {tooltip.content.assetDetails.zone}
                                                        </span>
                                                    </div>
                                                )}
                                                {tooltip.content.assetDetails.switchInfo && tooltip.content.assetDetails.switchInfo !== 'N/A' && (
                                                    <div className={styles.tooltip_row}>
                                                        <span className={styles.tooltip_label}>Switch:</span>
                                                        <span className={styles.tooltip_value}>
                                                            {tooltip.content.assetDetails.switchInfo}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Loading indicator */}
                                        {tooltip.content.loading && (
                                            <div className={styles.tooltip_section}>
                                                <div style={{ textAlign: 'center', color: '#999', fontSize: '11px', fontStyle: 'italic' }}>
                                                    Loading additional details...
                                                </div>
                                            </div>
                                        )}

                                        {/* Activity Information Section */}
                                        <div className={styles.tooltip_section}>
                                            <div className={styles.tooltip_section_title}>Activity Information</div>
                                            <div className={styles.tooltip_row}>
                                                <span className={styles.tooltip_label}>Total Connections:</span>
                                                <span className={`${styles.tooltip_value} ${styles.tooltip_connections}`}>
                                                    {tooltip.content.data.totalConnections?.toLocaleString() || 'N/A'}
                                                </span>
                                            </div>
                                            <div className={styles.tooltip_row}>
                                                <span className={styles.tooltip_label}>Activity Level:</span>
                                                <span className={styles.tooltip_value}>
                                                    {getNodeSizeCategory(tooltip.content.data.size)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                // Enhanced Edge Tooltip
                                <>
                                    <div className={styles.enhanced_tooltip_header}>
                                        <div className={styles.tooltip_icon}>🔗</div>
                                        <div>
                                            <div className={styles.tooltip_title}>Network Connection</div>
                                            <div className={styles.tooltip_subtitle}>Traffic Flow</div>
                                        </div>
                                    </div>
                                    
                                    <div className={styles.enhanced_tooltip_content}>
                                        {/* Connection Endpoints Section */}
                                        <div className={styles.tooltip_section}>
                                            <div className={styles.tooltip_section_title}>Connection Endpoints</div>
                                            <div className={styles.tooltip_row}>
                                                <span className={styles.tooltip_label}>Source IP:</span>
                                                <span className={`${styles.tooltip_value} ${styles.tooltip_ip}`}>
                                                    {tooltip.content.data.source}
                                                </span>
                                            </div>
                                            <div className={styles.tooltip_row}>
                                                <span className={styles.tooltip_label}>Destination IP:</span>
                                                <span className={`${styles.tooltip_value} ${styles.tooltip_ip}`}>
                                                    {tooltip.content.data.target}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Protocol & Ports Section */}
                                        <div className={styles.tooltip_section}>
                                            <div className={styles.tooltip_section_title}>Protocols & Ports</div>
                                            {formatProtocolsList(tooltip.content.data.protocols).map((group, index) => (
                                                <div key={index} className={styles.tooltip_row}>
                                                    <span className={styles.tooltip_label}>{group.protocol}:</span>
                                                    <span className={`${styles.tooltip_value} ${styles.tooltip_ports}`}>
                                                        {group.ports.length > 5 
                                                            ? `${group.ports.slice(0, 5).join(', ')} (+${group.ports.length - 5} more)`
                                                            : group.ports.join(', ')
                                                        }
                                                    </span>
                                                </div>
                                            ))}
                                            {(!tooltip.content.data.protocols || tooltip.content.data.protocols.length === 0) && (
                                                <div className={styles.tooltip_row}>
                                                    <span className={styles.tooltip_label}>Protocols:</span>
                                                    <span className={styles.tooltip_value}>No protocol data</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Traffic Statistics Section */}
                                        <div className={styles.tooltip_section}>
                                            <div className={styles.tooltip_section_title}>Traffic Statistics</div>
                                            <div className={styles.tooltip_row}>
                                                <span className={styles.tooltip_label}>Total Connections:</span>
                                                <span className={`${styles.tooltip_value} ${styles.tooltip_connections}`}>
                                                    {tooltip.content.data.connectionCount?.toLocaleString() || 'N/A'}
                                                </span>
                                            </div>
                                            <div className={styles.tooltip_row}>
                                                <span className={styles.tooltip_label}>Connection Strength:</span>
                                                <span className={styles.tooltip_value}>
                                                    {getConnectionStrength(tooltip.content.data.connectionCount)}
                                                </span>
                                            </div>
                                            {tooltip.content.data.weight && (
                                                <div className={styles.tooltip_row}>
                                                    <span className={styles.tooltip_label}>Visual Weight:</span>
                                                    <span className={styles.tooltip_value}>
                                                        {tooltip.content.data.weight.toFixed(1)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Enhanced Legend with Minimize/Expand */}
                    <div className={`${styles.enhanced_legend} ${!showLegend ? styles.legend_minimized : ''}`}>
                        <div className={styles.legend_header}>
                            <div className={styles.legend_title}>Network Legend</div>
                            <button
                                onClick={() => setShowLegend(!showLegend)}
                                className={styles.legend_toggle}
                                title={showLegend ? 'Minimize Legend' : 'Expand Legend'}
                            >
                                {showLegend ? '−' : '+'}
                            </button>
                        </div>
                        
                        {showLegend && (
                            <>
                                <div className={styles.legend_items}>
                                    {viewMode === 'zone' ? (
                                        <>
                                            {Object.values((networkData.nodes || []).reduce((acc, n) => {
                                                const z = (n.data && n.data.zone) || 'Unzoned';
                                                if (!acc[z]) acc[z] = { zone: z, color: (n.data && n.data.color) || '#9aa0a6' };
                                                return acc;
                                            }, {})).sort((a, b) => a.zone.localeCompare(b.zone)).map(z => (
                                                <div className={styles.legend_item} key={z.zone}>
                                                    <div className={styles.legend_color} style={{ backgroundColor: z.color, borderColor: z.color }}></div>
                                                    <span>{z.zone}</span>
                                                </div>
                                            ))}
                                            <div className={styles.legend_item}>
                                                <div className={styles.legend_arrow}></div>
                                                <span>Traffic Flow & Volume</span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className={styles.legend_item}>
                                                <div className={`${styles.legend_color} ${styles.known_color}`}></div>
                                                <span>Internal IPs (Private Networks)</span>
                                            </div>
                                            <div className={styles.legend_item}>
                                                <div className={styles.legend_color} style={{
                                                    backgroundColor: '#F44336',
                                                    borderColor: '#C62828'
                                                }}></div>
                                                <span>External IPs (Public Networks)</span>
                                            </div>
                                            <div className={styles.legend_item}>
                                                <div className={styles.legend_arrow}></div>
                                                <span>Traffic Flow & Volume</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                                
                            </>
                        )}
                    </div>
                </div>

                {/* Removed Selected Node Info section */}
            </div>
        </div>
    );
};

export default NetworkTrafficModal;
